import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

type LanguageCode = 'en' | 'es';

interface LanguageOption {
  code: LanguageCode;
  name: string;
  switchLabel: string;
}

const DEFAULT_LANGUAGE: LanguageCode = 'en';
const STORAGE_KEY = 'lang';

const LANGUAGES: Record<LanguageCode, LanguageOption> = {
  en: {
    code: 'en',
    name: 'English',
    switchLabel: 'Cambiar a español',
  },
  es: {
    code: 'es',
    name: 'Español',
    switchLabel: 'Switch to English',
  },
};

@Component({
  selector: 'don-language-selector',
  imports: [CommonModule, TranslateModule],
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  @Input() size: 'mobile' | 'desktop' = 'desktop';
  @Output() languageChange = new EventEmitter<LanguageCode>();

  current: LanguageCode = DEFAULT_LANGUAGE;

  private readonly subs = new Subscription();

  constructor(private readonly translate: TranslateService) {}

  ngOnInit(): void {
    this.setInitialLanguage();
    this.listenToTranslateChanges();
    window.addEventListener('storage', this.onStorage);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    window.removeEventListener('storage', this.onStorage);
  }

  get display(): string {
    return this.current.toUpperCase();
  }

  get currentLanguageName(): string {
    return LANGUAGES[this.current].name;
  }

  get nextLanguageName(): string {
    return LANGUAGES[this.getNextLanguage()].name;
  }

  get switchLabel(): string {
    return LANGUAGES[this.current].switchLabel;
  }

  get ariaLabel(): string {
    return `Current language: ${this.currentLanguageName}. Click to switch to ${this.nextLanguageName}.`;
  }

  toggle(): void {
    this.change(this.getNextLanguage());
  }

  change(lang: string): void {
    const normalized = this.normalizeLanguage(lang);

    if (normalized === this.current) {
      return;
    }

    this.current = normalized;
    this.translate.use(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
    this.languageChange.emit(normalized);
  }

  private setInitialLanguage(): void {
    const fromTranslate = this.translate.currentLang;
    const fromStorage = localStorage.getItem(STORAGE_KEY);

    const initialLanguage = this.normalizeLanguage(fromTranslate || fromStorage);

    this.current = initialLanguage;
    this.translate.use(initialLanguage);
  }

  private listenToTranslateChanges(): void {
    this.subs.add(
      this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.current = this.normalizeLanguage(event.lang);
      })
    );
  }

  private getNextLanguage(): LanguageCode {
    return this.current === 'en' ? 'es' : 'en';
  }

  private normalizeLanguage(lang?: string | null): LanguageCode {
    const normalized = (lang || DEFAULT_LANGUAGE).toLowerCase();

    return this.isSupportedLanguage(normalized) ? normalized : DEFAULT_LANGUAGE;
  }

  private isSupportedLanguage(lang: string): lang is LanguageCode {
    return lang === 'en' || lang === 'es';
  }

  private onStorage = (event: StorageEvent): void => {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    const newLanguage = this.normalizeLanguage(event.newValue);

    if (newLanguage !== this.current) {
      this.current = newLanguage;
      this.translate.use(newLanguage);
    }
  };
}
